import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft2 } from 'iconsax-react';
import type { PropsWithChildren } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useTypedSearchParams } from 'react-router-typesafe-routes';
import { z } from 'zod';
import ProgressButton from '@/components/common/progress-button';
import { FieldMessage } from '@/components/fields/field-message';
import { OneTimeCodeField } from '@/components/fields/one-time-code-field';
import { PhoneField } from '@/components/fields/phone-field';
import ROUTES from '@/routes';
import { zodErrorMap } from '@/utils/zod-error-map';

interface Props {
  isLoading?: boolean;
  disabled?: boolean;
  navigateBack?: boolean;
  onSubmit: (data: SignUpFormData) => Promise<boolean>;
}

export function SignUpForm({
  disabled = false,
  isLoading = false,
  onSubmit,
  navigateBack = false,
  children,
}: PropsWithChildren<Props>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [{ phone }] = useTypedSearchParams(ROUTES.AUTH.SIGN_IN);
  const form = useForm<SignUpFormData>({
    resolver: zodResolver(signUpFormSchema, { errorMap: zodErrorMap }),
    defaultValues: async () => {
      if (phone?.includes(' ')) {
        const [countryCode = '', phoneNumber = ''] = phone.split(' ');
        return {
          countryCode,
          phoneNumber,
          oneTimeCode: '',
        };
      }

      return {
        countryCode: '86',
        phoneNumber: '',
        oneTimeCode: '',
      };
    },
  });

  const doSubmit = async (data: SignUpFormData) => {
    if (await onSubmit(data)) {
      form.reset();
    }
  };

  return (
    <FormProvider {...form}>
      <form
        className='flex size-full flex-1 flex-col'
        onSubmit={form.handleSubmit(doSubmit)}>
        <div className='form-control w-full'>
          <PhoneField
            disabled={!!phone || isLoading}
            names={['countryCode', 'phoneNumber']}
            placeholder={t('auth.phone-number')}
            control={form.control}
          />

          <FieldMessage
            name='phoneNumber'
            errors={form.formState.errors}
          />
        </div>

        <div className='form-control mt-4 w-full'>
          <OneTimeCodeField
            disabled={disabled || isLoading}
            name='oneTimeCode'
            placeholder={t('auth.one-time-password')}
            type='sign-up'
          />

          <FieldMessage
            name='oneTimeCode'
            errors={form.formState.errors}
          />
        </div>

        <div className='flex-1'>{children}</div>

        <div className='form-control flex gap-4 flex-row mt-8'>
          {navigateBack && (
            <button
              type='button'
              className='absolute desktop:static left-4 top-safe-offset-4 btn btn-square desktop:btn-circle btn-ghost desktop:border text-primary-content'
              onClick={() => navigate(-1)}>
              <ArrowLeft2 className='aspect-square size-6' />
            </button>
          )}

          <ProgressButton
            as='submit'
            disabled={disabled}
            isLoading={isLoading}
            className='btn btn-revert-primary flex-1'
            label='auth.sign-up'
          />
        </div>
      </form>
    </FormProvider>
  );
}

const signUpFormSchema = z.object({
  countryCode: z.string(),
  phoneNumber: z.string().min(8, 'errors.string.length|count=8').regex(/^\d+$/),
  oneTimeCode: z.string().min(6, 'errors.string.length|count=6'),
});

export type SignUpFormData = z.infer<typeof signUpFormSchema>;
